import { z } from "zod";
import type { FilterOption, IntegrationFilterOption, GetDbtModelsParams } from "@/types/dbtModels";

export type FilterOptionWithValue = FilterOption & { _value?: string[] };

// Keys of GetDbtModelsParams that accept string[] filter values (excludes page/size/search)
export type DbtModelFilterKey = Exclude<keyof GetDbtModelsParams, "page" | "size" | "search">;
export type DbtModelFilterAccumulator = Partial<Record<DbtModelFilterKey, string[]>>;
export type ComputedFilters = Record<string, FilterOptionWithValue[] | IntegrationFilterOption[]>;

// Tag schema (defined first — used by columnSchema and dbtModelSchema)
export const TagSchema = z.object({
  rk: z.string().nullable().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
  description: z.string().optional(),
});

export type { DbtModel } from "@/types/dbtModels";

export interface StartingNodeDBTParams {
  table_id: number | string;
}

export interface GetSQLLineageParams {
  dbt_model_rk: string;
  session_id: string;
}

export interface PostColumnsDBTParams {
  table_id: number | string;
}

export interface TestMetadata {
  name: string;
}

export interface Tag {
  rk: string;
  name?: string;
  type?: string;
}

export interface Edge {
  src: string;
  dst: string;
}

export interface PostConnectedColumnsDBTParams {
  column_fqns: string[];
  edges?: Edge[];
  nodes?: string[];
  upstreamExpansion?: boolean;
  showIndirectEdges?: boolean;
}

// Zod schemas for API responses

// Test metadata schema
export const TestMetadataSchema = z.object({
  name: z.string(),
});

// Table node response schema (for terminal, upstream, downstream)
export const TableNodeResponseSchema = z.object({
  table: z.string(),
  upstream_count: z.number(),
  downstream_count: z.number(),
  id: z.union([z.number(), z.string()]).nullable().optional(),
  materialization: z.string().nullable().optional(),
  label: z.string(),
  schema_name: z.string().nullable().optional(),
  resource_type: z.string().nullable().optional(),
  tests: z.array(TestMetadataSchema).nullable().optional(),
  source: z.string().nullable().optional(),
  is_stale: z.boolean().nullable().optional(),
  last_seen: z.string().nullable().optional(),
});

export type TableNodeResponse = z.infer<typeof TableNodeResponseSchema>;

// Connected tables response schema (for upstream/downstream)
export const ConnectedTablesResponseSchema = z.object({
  tables: z.array(TableNodeResponseSchema),
});

export type ConnectedTablesResponse = z.infer<typeof ConnectedTablesResponseSchema>;

// Schema metadata schema (for table schema columns)
export const SchemaMetadataSchema = z.object({
  table_schema_id: z.number().nullable().optional(),
  rk: z.string(),
  name: z.string(),
  datatype: z.string(),
  description: z.string().nullable().optional(),
  tags: z.array(TagSchema),
  can_lineage_expand: z.boolean(),
  is_transformation: z.boolean().optional(),
});

export type SchemaMetadata = z.infer<typeof SchemaMetadataSchema>;

// Table schema response schema
export const TableSchemaResponseSchema = z.object({
  table: z.string(),
  columns: z.array(SchemaMetadataSchema),
  purpose: z.string().nullable().optional(),
  last_modified_by: z.number().nullable().optional(),
});

export type TableSchemaResponse = z.infer<typeof TableSchemaResponseSchema>;

// Column lineage edge schema
export const ColumnLineageEdgeSchema = z.object({
  source: z.string(),
  target: z.string(),
  extra: z.record(z.unknown()).nullable().optional(),
});

// Column lineage response schema (for connected columns)
export const ColumnLineageResponseSchema = z.object({
  highlight_edges: z.array(ColumnLineageEdgeSchema),
});

export type ColumnLineageResponse = z.infer<typeof ColumnLineageResponseSchema>;

// SQL lineage response schema
export const SQLLineageResponseSchema = z.object({
  tableEdges: z.array(z.tuple([z.string(), z.string()])),
  details: z.record(z.unknown()),
  nodePositions: z
    .record(z.tuple([z.number(), z.number()]))
    .nullable()
    .optional(),
});

export type SQLLineageResponse = z.infer<typeof SQLLineageResponseSchema>;

// Integration Environment Schema (matches backend DBTCoreIntegrationEnvironment)
export const IntegrationEnvironmentSchema = z.object({
  id: z.number(),
  dbt_core_integration_id: z.number(),
  environment_type: z.string(),
  created_at: z.string(),
  last_modified_at: z.string(),
  created_by: z.number(),
  last_modified_by: z.number(),
});

export type IntegrationEnvironment = z.infer<typeof IntegrationEnvironmentSchema>;

// Integration Environments Response Schema (array of environments)
export const IntegrationEnvironmentsResponseSchema = z.array(IntegrationEnvironmentSchema);

export type IntegrationEnvironmentsResponse = z.infer<typeof IntegrationEnvironmentsResponseSchema>;
